import React, { useEffect, useState } from "react";
import PageHeader, { HeaderButton, HeaderLink } from "../karkhana/ui/PageHeader";
import notifications from "../../common/Components/Notifications/Notification";
import {
  Meter,
  PartnerRow,
  inviteToWorkspace,
  invitePartner,
  listPartners,
  lookupPartner,
  partnerAction,
  setPartnerGroups,
} from "../../services/partnerNetworkApi";
import { buyStudioModule } from "../../helpers/saasCheckout";
import { GroupsEditor, HealthDot, MODE_LABEL, STATUS_LABEL } from "./pnUi";
import "../karkhana/ui/karkhana-ui.scss";
import "./partner-network.scss";

const MODES = [
  { id: "HKP_EXISTING", title: "Already on HKP", body: "Find them by phone or GST. They accept in their own workspace." },
  { id: "SPONSORED", title: "Give them a workspace", body: "You pay; they own their workspace and data. They sign in with a phone code." },
  { id: "LINK_ONLY", title: "No account (link only)", body: "Each order gets a phone link. No app, no login. What they send belongs to you." },
];

export const PartnerMeter = ({ meter, onAdd, adding }: { meter: Meter; onAdd: () => void; adding: boolean }) => {
  const full = meter.used >= meter.limit;
  const pct = meter.limit ? Math.min(1, meter.used / meter.limit) : 1;
  return (
    <div className="pn-card">
      <div className={`pn-meter${full ? " is-full" : ""}`}>
        <span className="pn-meter__text">
          {meter.used} of {meter.limit} {meter.extra ? `(${meter.included} included + ${meter.extra} added)` : "included"}
        </span>
        <div className="pn-meter__bar" role="progressbar" aria-valuemin={0} aria-valuemax={meter.limit} aria-valuenow={meter.used}>
          <span style={{ transform: `scaleX(${pct})` }} />
        </div>
        <button type="button" className="kh-btn" onClick={onAdd} disabled={adding}>
          + Add a partner
        </button>
      </div>
      {full ? <p className="pn-limit">You've reached your partner limit. Add more partners to invite another.</p> : null}
      {!meter.hasNetwork ? (
        <p className="pn-limit">Sending production orders needs the Network plan. Partners receive orders free.</p>
      ) : null}
    </div>
  );
};

const PartnersPage = () => {
  const [rows, setRows] = useState<PartnerRow[]>([]);
  const [meter, setMeter] = useState<Meter | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingGroups, setEditingGroups] = useState("");

  const load = async () => {
    const res = await listPartners();
    setLoading(false);
    if (res.success) {
      setRows(res.data.partners);
      setMeter(res.data.meter);
    } else {
      notifications({ title: "Partners", message: res.message, type: "danger" });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const addSlot = async () => {
    setAdding(true);
    const error = await buyStudioModule("NETWORK_EXTRA_PARTNER", "One more partner");
    setAdding(false);
    if (error) {
      notifications({ title: "Add a partner", message: error, type: "danger" });
    } else {
      notifications({ title: "Partner added", message: "You can invite one more partner.", type: "success" });
      load();
    }
  };

  const act = async (row: PartnerRow, action: "pause" | "resume" | "end") => {
    if (action === "end" && !window.confirm(`End the partnership with ${row.name}? Shared history stays read-only for both of you.`)) {
      return;
    }
    const res = await partnerAction(row.id, action);
    if (res.success) load();
    else notifications({ title: "Partner", message: res.message, type: "danger" });
  };

  const full = meter ? meter.used >= meter.limit : false;

  return (
    <div className="pn-page">
      <PageHeader title="Partners">
        <HeaderLink to="/partner-network/board">Production board</HeaderLink>
        <HeaderButton primary add onClick={() => (full ? addSlot() : setInviting(true))}>
          Invite partner
        </HeaderButton>
      </PageHeader>
      {meter ? <PartnerMeter meter={meter} onAdd={addSlot} adding={adding} /> : null}
      <div className="pn-card">
        {loading ? <p className="pn-muted">Loading…</p> : null}
        {!loading && !rows.length ? (
          <p className="pn-muted">No partners yet. Invite a studio, cooperative or master weaver to send them orders.</p>
        ) : null}
        <ul className="pn-list">
          {rows.map((row) => (
            <li key={row.id}>
              <div>
                <div className="pn-list__name">{row.name}</div>
                <div className="pn-list__meta">
                  <span className="pn-chip is-blue">{MODE_LABEL[row.mode]}</span>
                  <span className="pn-chip">{STATUS_LABEL[row.status]}</span>
                  {row.pendingMode ? <span className="pn-chip is-warn">Workspace invite sent</span> : null}
                  <span>{row.activeOrders} active order{row.activeOrders === 1 ? "" : "s"}</span>
                  {row.groupIds?.map((g) => <span key={g} className="pn-chip">{g}</span>)}
                </div>
                {editingGroups === row.id ? (
                  <GroupsEditor value={row.groupIds || []} onCancel={() => setEditingGroups("")}
                    onSave={async (groups) => {
                      const res = await setPartnerGroups(row.id, groups);
                      if (res.success) {
                        setEditingGroups("");
                        load();
                      } else {
                        notifications({ title: "Groups", message: res.message, type: "danger" });
                      }
                    }} />
                ) : null}
              </div>
              <div className="pn-list__side">
                {row.health ? <HealthDot health={row.health} withLabel /> : null}
                {row.mode === "LINK_ONLY" && row.status === "ACTIVE" && !row.pendingMode ? (
                  <button className="kh-btn" onClick={async () => {
                    const res = await inviteToWorkspace(row.id);
                    notifications({ title: "Invite", message: res.success ? "Invite sent by SMS." : res.message, type: res.success ? "success" : "danger" });
                    load();
                  }}>Invite to own workspace</button>
                ) : null}
                {row.status !== "ENDED" && editingGroups !== row.id ? (
                  <button className="kh-btn" onClick={() => setEditingGroups(row.id)}>Groups</button>
                ) : null}
                {row.status === "ACTIVE" ? <button className="kh-btn" onClick={() => act(row, "pause")}>Pause</button> : null}
                {row.status === "PAUSED" ? <button className="kh-btn" onClick={() => act(row, "resume")}>Resume</button> : null}
                {row.status !== "ENDED" ? <button className="kh-btn" onClick={() => act(row, "end")}>End</button> : null}
              </div>
            </li>
          ))}
        </ul>
      </div>
      {inviting ? <InviteDialog onClose={() => setInviting(false)} onDone={() => { setInviting(false); load(); }} /> : null}
    </div>
  );
};

const InviteDialog = ({ onClose, onDone }: { onClose: () => void; onDone: () => void }) => {
  const [mode, setMode] = useState("LINK_ONLY");
  const [phone, setPhone] = useState("");
  const [gst, setGst] = useState("");
  const [name, setName] = useState("");
  const [language, setLanguage] = useState("hi");
  const [karkhana, setKarkhana] = useState(false);
  const [groups, setGroups] = useState("");
  const [found, setFound] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    if (mode === "HKP_EXISTING" && !found) {
      const res = await lookupPartner(phone, gst);
      setBusy(false);
      if (res.success && res.data?.found) setFound(res.data);
      else setError("No HKP studio has that phone or GST. Give them a workspace or use a link instead.");
      return;
    }
    const res = await invitePartner({
      mode,
      partnerOrgId: found?.orgId,
      phone,
      gst,
      name,
      language,
      includeKarkhana: karkhana,
      groupIds: groups.split(",").map((g) => g.trim()).filter(Boolean),
    });
    setBusy(false);
    if (!res.success) {
      setError(res.message || "Could not invite");
      return;
    }
    if (res.data.inviteUrl) setInviteUrl(res.data.inviteUrl);
    else onDone();
  };

  return (
    <div className="pn-modal" role="dialog" aria-modal="true" aria-label="Invite partner" onClick={onClose}>
      <div className="pn-modal__box pn-page" onClick={(e) => e.stopPropagation()}>
        <h3>Invite a partner</h3>
        {inviteUrl ? (
          <>
            <p>We sent them an SMS. You can also share this link on WhatsApp:</p>
            <p className="pn-note" style={{ wordBreak: "break-all" }}>{inviteUrl}</p>
            <div className="pn-actions">
              <button className="kh-btn" onClick={() => navigator.clipboard?.writeText(inviteUrl)}>Copy link</button>
              <button className="kh-btn is-primary" onClick={onDone}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div className="pn-policy" role="radiogroup">
              {MODES.map((m) => (
                <label key={m.id} className="pn-mode">
                  <input type="radio" name="mode" checked={mode === m.id} onChange={() => { setMode(m.id); setFound(null); }} />{" "}
                  <b>{m.title}</b>
                  <div className="pn-muted">{m.body}</div>
                </label>
              ))}
            </div>
            <div className="pn-form" style={{ marginTop: 12 }}>
              <label>
                Phone
                <input inputMode="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setFound(null); }} />
              </label>
              {mode === "HKP_EXISTING" ? (
                <label>
                  or GST
                  <input value={gst} onChange={(e) => { setGst(e.target.value); setFound(null); }} />
                </label>
              ) : (
                <>
                  <label>
                    Name
                    <input value={name} onChange={(e) => setName(e.target.value)} />
                  </label>
                  <label>
                    Language
                    <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                      <option value="hi">Hindi</option>
                      <option value="bn">Bengali</option>
                      <option value="ta">Tamil</option>
                      <option value="en">English</option>
                    </select>
                  </label>
                </>
              )}
              <label className="is-wide">
                Groups (optional, comma-separated)
                <input value={groups} placeholder="Bengal jamdani, Shantipur" onChange={(e) => setGroups(e.target.value)} />
              </label>
              {mode === "SPONSORED" ? (
                <label className="is-wide" style={{ flexDirection: "row", alignItems: "center" }}>
                  <input type="checkbox" checked={karkhana} onChange={(e) => setKarkhana(e.target.checked)} style={{ minHeight: 0 }} />
                  Include Karkhana (looms and wages, private to them)
                </label>
              ) : null}
            </div>
            {found ? <p className="pn-note">Found <b>{found.name}</b>{found.city ? `, ${found.city}` : ""}. They'll get a request in HKP and by SMS.</p> : null}
            {error ? <p className="pn-limit">{error}</p> : null}
            <div className="pn-actions">
              <button className="kh-btn" onClick={onClose}>Cancel</button>
              <button className="kh-btn is-primary" disabled={busy} onClick={submit}>
                {mode === "HKP_EXISTING" && !found ? "Find studio" : "Send invite"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PartnersPage;
